import { useEffect, useState } from 'react';
import {
  CreateVirtualAccountRequest,
  FetchVirtualAccountsRequest,
  VirtualAccount,
  VirtualAccountCurrency,
} from 'vopay';
import axios from 'axios';
import vopayApi from '@/services/api.config';

const { api } = vopayApi;

export default function useVirtualAccounts() {
  const source = axios.CancelToken.source();
  const [pending, setPending] = useState(false);
  const [virtualAccount, setVirtualAccount] = useState<VirtualAccount | null>(null);
  const [virtualAccounts, setVirtualAccounts] = useState<VirtualAccount[]>([]);
  const [virtualAccountCurrencies, setVirtualAccountCurrencies] = useState<VirtualAccountCurrency[]>([]);

  useEffect(() => {
    return () => source.cancel();
  }, []);

  async function createVirtualAccount(req: CreateVirtualAccountRequest) {
    try {
      setPending(true);

      const res = await api.createVirtualAccount(req, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setPending(false);
    }
  }

  async function fetchVirtualAccount(virtualAccountID: string) {
    try {
      setPending(true);

      const res = await api.fetchVirtualAccounts({}, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      const virtualAccount = res.VirtualAccounts.find((a) => a.VirtualAccountID === virtualAccountID) || null;
      setVirtualAccount(virtualAccount);

      return virtualAccount;
    } finally {
      setPending(false);
    }
  }

  async function fetchVirtualAccounts(params: FetchVirtualAccountsRequest = {}) {
    try {
      setPending(true);
      const res = await api.fetchVirtualAccounts(params, source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setVirtualAccounts(res.VirtualAccounts);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setPending(false);
    }
  }

  async function fetchVirtualAccountCurrencies() {
    try {
      setPending(true);
      const res = await api.fetchVirtualAccountCurrencies(source);

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      setVirtualAccountCurrencies(res.VirtualAccounts);

      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setPending(false);
    }
  }

  async function simulateVirtualAccountCredit(virtualAccountID: string, amount: string) {
    try {
      setPending(true);
      const res = await api.simulateVirtualAccountCredit(
        {
          VirtualAccountID: virtualAccountID,
          Amount: amount,
        },
        source,
      );

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setPending(false);
    }
  }

  return {
    pending,
    createVirtualAccount,
    virtualAccount,
    fetchVirtualAccount,
    virtualAccounts,
    fetchVirtualAccounts,
    virtualAccountCurrencies,
    fetchVirtualAccountCurrencies,
    simulateVirtualAccountCredit,
  };
}
